import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import ErrorBoundary from './components/ErrorBoundary'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { useEffect } from 'react'

import BottomNav from './components/layout/BottomNav.jsx'
import PromoBanner from './components/PromoBanner.jsx'
import PromoPopup from './components/PromoPopup.jsx'
import ProtectedRoute from './components/ProtectedRoute.jsx'
import Home from './pages/Home.jsx'
import Shop from './pages/Shop.jsx'
import ProductDetail from './pages/ProductDetail.jsx'
import Cart from './pages/Cart.jsx'
import SearchPage from './pages/Search.jsx'
import Orders from './pages/Orders.jsx'
import OrderDetail from './pages/OrderDetail.jsx'
import Account from './pages/Account.jsx'
import Login from './pages/Login.jsx'
import Checkout from './pages/Checkout.jsx'
import { OrderSuccess, Notifications } from './pages/misc.jsx'
import Promotions from './pages/Promotions.jsx'
import Templates from './pages/Templates.jsx'
import Wishlist from './pages/Wishlist.jsx'
import { useAppStore, useThemeStore } from './store/index.js'
import PWAUpdatePrompt from './components/PWAUpdatePrompt.jsx'
import IOSInstallPrompt from './components/IOSInstallPrompt.jsx'

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5 * 60 * 1000, retry: 1 } }
})

// Pages that should NOT show the bottom nav
const NO_NAV_ROUTES = ['/login', '/register', '/checkout', '/order-success', '/cart', '/templates']

function AppLayout() {
  const location = useLocation()
  // Also hide nav on product detail pages (they have their own fixed CTA bar)
  const isProductDetail = location.pathname.startsWith('/product/')
  const showNav = !NO_NAV_ROUTES.includes(location.pathname) && !isProductDetail
  const isOnline = useAppStore(s => s.isOnline)
  const setOnline = useAppStore(s => s.setOnline)
  const setInstallPrompt = useAppStore(s => s.setInstallPrompt)
  const applyTheme = useThemeStore(s => s.apply)

  // Apply persisted theme on mount (handles page refresh)
  useEffect(() => { applyTheme() }, [])

  // Online/offline detection
  useEffect(() => {
    const onOnline = () => setOnline(true)
    const onOffline = () => setOnline(false)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline) }
  }, [])

  // PWA install prompt (captured in usePWA hook; keep store in sync for legacy consumers)
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e) }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])


  return (
    <>
      <PromoBanner />
      <PromoPopup />
      {/* Offline indicator */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gray-900 border-b border-gray-700 px-4 py-2 flex items-center justify-center gap-2 text-xs font-display">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
          <span className="text-gray-300">You're offline — some features may be limited</span>
        </div>
      )}
      <ErrorBoundary>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />

        {/* Home is protected — must be logged in to browse (matches customer site) */}
        <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />

        {/* Protected — must be logged in to browse catalog */}
        <Route path="/shop" element={<ProtectedRoute><Shop /></ProtectedRoute>} />
        <Route path="/product/:id" element={<ProtectedRoute><ProductDetail /></ProtectedRoute>} />
        <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
        <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
        <Route path="/orders/:id" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
        <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
        <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
        <Route path="/order-success" element={<ProtectedRoute><OrderSuccess /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
        <Route path="/promotions" element={<ProtectedRoute><Promotions /></ProtectedRoute>} />
        <Route path="/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
        <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />

        <Route path="*" element={<ProtectedRoute><Home /></ProtectedRoute>} />
      </Routes>
      </ErrorBoundary>
      {showNav && (
        <div
          className="fixed left-0 right-0 z-40 flex justify-start items-center px-3 py-1.5 bg-surface-card/80 backdrop-blur-sm border-t border-surface-border"
          style={{ bottom: 'calc(57px + max(0.75rem, env(safe-area-inset-bottom, 0px)))' }}
        >
          <a
            href="https://www.aware.org.za"
            target="_blank"
            rel="noopener noreferrer"
            title="No alcohol may be sold to persons under the age of 18"
          >
            <img
              src="/aware-logo.webp"
              alt="No Underage Drinking — aware.org.za"
              className="h-8 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
            />
          </a>
        </div>
      )}
      {showNav && <BottomNav />}
    </>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppLayout />
        <Toaster
          position="top-center"
          toastOptions={{
            style: {
              background: '#1c1c1c',
              color: '#f3f4f6',
              border: '1px solid #2a2a2a',
              borderRadius: '14px',
              fontFamily: '"Exo 2", sans-serif',
              fontSize: '13px',
              fontWeight: '500',
            },
            success: { iconTheme: { primary: '#0e947a', secondary: '#fff' } }
          }}
        />
        <PWAUpdatePrompt />
        <IOSInstallPrompt />
      </BrowserRouter>
    </QueryClientProvider>
  )
}
